import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import type { SxProps, Theme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { CaretLeft, CaretRight } from '@phosphor-icons/react';
import type { ReactNode, SyntheticEvent } from 'react';
import { forwardRef, useEffect, useId, useState } from 'react';
import { Autocomplete } from '../Autocomplete';
import { TextField } from '../TextField';

/**
 * A single "rows per page" choice — either a bare number, or a `{ value, label }` pair for a
 * named option like `{ value: -1, label: 'All' }`.
 */
export type RowsPerPageOption = number | { value: number; label: string };

interface NormalizedOption {
  value: number;
  label: string;
}

export interface TablePaginationProps {
  /** Total number of rows across all pages. */
  count: number;
  /** Current page, **zero-based** (MUI convention). The page field shows `page + 1`. */
  page: number;
  /** Rows shown per page. Use `-1` together with a `{ value: -1, label: 'All' }` option for "All". */
  rowsPerPage: number;
  /** Options for the rows-per-page `Autocomplete`. Empty array hides the rows-per-page control. */
  rowsPerPageOptions?: RowsPerPageOption[];
  /** Called with the new **zero-based** page when the user navigates (arrows or the page field). */
  onPageChange?: (event: SyntheticEvent | null, newPage: number) => void;
  /** Called with the chosen rows-per-page value. */
  onRowsPerPageChange?: (value: number) => void;
  /** Leading label. Default: `"Rows per page:"`. */
  labelRowsPerPage?: ReactNode;
  /** Renders the "from–to of count" text. Default: `` `${from}–${to} of ${count}` ``. */
  labelDisplayedRows?: (info: { from: number; to: number; count: number; page: number }) => ReactNode;
  /** aria-label for the prev/next buttons. */
  getItemAriaLabel?: (type: 'previous' | 'next') => string;
  /** Disables every control. */
  disabled?: boolean;
  className?: string;
  sx?: SxProps<Theme>;
  id?: string;
}

function normalizeOption(option: RowsPerPageOption): NormalizedOption {
  return typeof option === 'number' ? { value: option, label: String(option) } : option;
}

/**
 * TablePagination — the table footer bar. LOCAL to the Table family.
 *
 * A custom composition (NOT MUI's `TablePagination`, which hard-codes a native `<Select>` and
 * cannot host an `Autocomplete`/page field). Assembles the design system's already-themed
 * primitives per Figma (node 1390:4870):
 *   [ "Rows per page:" ] [ Autocomplete ] [ "1–10 of 37" ] [ ‹ IconButton  •  TextField  •  IconButton › ]
 *
 * Controlled: the app owns `page`/`rowsPerPage` state and slices the data itself — this component
 * only renders the controls. Navigation is prev/next arrows plus a page-number field the user can
 * type into (no first/last buttons, per the Figma redesign).
 */
export const TablePagination = forwardRef<HTMLDivElement, TablePaginationProps>(function TablePagination(props, ref) {
  const {
    count,
    page,
    rowsPerPage,
    rowsPerPageOptions = [5, 10, 25],
    onPageChange,
    onRowsPerPageChange,
    labelRowsPerPage = 'Rows per page:',
    labelDisplayedRows,
    getItemAriaLabel,
    disabled = false,
    className,
    sx,
    id,
  } = props;

  const labelId = useId();

  const options = rowsPerPageOptions.map(normalizeOption);
  const currentOption = options.find(o => o.value === rowsPerPage) ?? normalizeOption(rowsPerPage);

  const pageCount = rowsPerPage > 0 ? Math.max(1, Math.ceil(count / rowsPerPage)) : 1;
  const from = count === 0 ? 0 : page * rowsPerPage + 1;
  const to = rowsPerPage === -1 ? count : Math.min(count, (page + 1) * rowsPerPage);

  // The page field is edit-while-typing; commit (clamp + navigate) on Enter/blur.
  const [pageInput, setPageInput] = useState(String(page + 1));
  useEffect(() => {
    setPageInput(String(page + 1));
  }, [page]);

  const commitPage = (event: SyntheticEvent) => {
    const parsed = parseInt(pageInput, 10);
    if (Number.isNaN(parsed)) {
      setPageInput(String(page + 1));
      return;
    }
    const clamped = Math.min(Math.max(parsed, 1), pageCount);
    if (clamped - 1 !== page) onPageChange?.(event, clamped - 1);
    setPageInput(String(clamped));
  };

  const isFirstPage = page <= 0;
  const isLastPage = page >= pageCount - 1;
  const ariaLabel = (type: 'previous' | 'next') => getItemAriaLabel?.(type) ?? `Go to ${type} page`;

  return (
    <Box
      ref={ref}
      id={id}
      className={className}
      sx={[
        {
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          justifyContent: 'flex-end',
          gap: 1.5, // sp12 — between the four groups
          p: 1, // sp8 — with the 36-tall controls this makes the 52-high bar (Figma)
        },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    >
      {options.length > 0 && (
        <>
          <Typography id={labelId} variant="body2" sx={{ color: 'text.secondary' }}>
            {labelRowsPerPage}
          </Typography>
          <Autocomplete
            disableClearable
            disabled={disabled}
            options={options}
            value={currentOption}
            getOptionLabel={option => option.label}
            isOptionEqualToValue={(option, value) => option.value === value.value}
            onChange={(_event, option) => {
              if (option) onRowsPerPageChange?.(option.value);
            }}
            // Figma shows a content-hugging pill, but a true CSS hug isn't robust for a
            // text-input + caret Autocomplete (native input defaults to ~20ch + flex-grow).
            // Fixed 72 (designer-approved fallback) fits "25"/"All" + the caret cleanly.
            sx={{ width: 72 }}
            renderInput={params => (
              <TextField
                {...params}
                size="medium"
                fullWidth
                slotProps={{
                  ...params.slotProps,
                  htmlInput: { ...params.slotProps.htmlInput, 'aria-labelledby': labelId },
                }}
              />
            )}
          />
        </>
      )}

      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
        {labelDisplayedRows ? labelDisplayedRows({ from, to, count, page }) : `${from}–${to} of ${count}`}
      </Typography>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 /* sp4 */ }}>
        <IconButton
          color="secondary"
          disabled={disabled || isFirstPage}
          aria-label={ariaLabel('previous')}
          onClick={event => onPageChange?.(event, page - 1)}
        >
          <CaretLeft />
        </IconButton>

        <TextField
          fullWidth
          disabled={disabled}
          value={pageInput}
          onChange={event => setPageInput(event.target.value)}
          onBlur={commitPage}
          onKeyDown={event => {
            if (event.key === 'Enter') {
              commitPage(event);
              (event.target as HTMLInputElement).blur();
            }
          }}
          slotProps={{
            htmlInput: { 'aria-label': 'Current page', inputMode: 'numeric' },
          }}
          sx={{ width: 48, '& .MuiInputBase-input': { textAlign: 'center' } }}
        />

        <IconButton
          color="secondary"
          disabled={disabled || isLastPage}
          aria-label={ariaLabel('next')}
          onClick={event => onPageChange?.(event, page + 1)}
        >
          <CaretRight />
        </IconButton>
      </Box>
    </Box>
  );
});
